import { useMemo, useRef, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useMotionStore } from './useMotionStore';
import * as THREE from 'three';
import { Line, TransformControls, Sphere } from '@react-three/drei';
import type { TransformControls as TransformControlsImpl } from 'three-stdlib';
import { ControlPoint } from './ControlPoint';

/**
 * Debug visualization for Bezier Curve blocks
 * Shows control points, control lines, and the curve path
 */
export const BezierCurveDebug = () => {
  const { blocks, activeBlockId, showBezierDebug } = useMotionStore();
  const transformControlRef = useRef<TransformControlsImpl>(null);
  const { camera, raycaster, pointer } = useThree();
  const controlPointRefsRef = useRef<Map<string, THREE.Mesh>>(new Map());
  const onDownPositionRef = useRef<THREE.Vector2>(new THREE.Vector2());
  
  // Handle pointer events similar to example
  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      onDownPositionRef.current.set(event.clientX, event.clientY);
    };
    
    const handlePointerUp = (event: MouseEvent) => {
      const onUpPosition = new THREE.Vector2(event.clientX, event.clientY);
      
      // If mouse didn't move, detach transform control (click without drag)
      if (onDownPositionRef.current.distanceTo(onUpPosition) === 0) {
        if (transformControlRef.current) {
          transformControlRef.current.detach();
        }
      }
    };
    
    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('pointerup', handlePointerUp);
    
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);
  
  // Use raycaster to detect hover and attach TransformControls (like example)
  const isDraggingRef = useRef(false);
  
  // Set up TransformControls event listeners
  useEffect(() => {
    const transformControl = transformControlRef.current;
    if (!transformControl) return;
    
    const handleChange = () => {
      // Update control point position when transform control moves
      const currentObject = (transformControl as any)?.object;
      if (currentObject) {
        const mesh = currentObject as THREE.Mesh;
        // Find which control point this mesh belongs to
        for (const [key, refMesh] of controlPointRefsRef.current.entries()) {
          if (refMesh === mesh) {
            const [blockId, pointIndexStr] = key.split('-');
            const pointIndex = parseInt(pointIndexStr);
            const currentBlocks = useMotionStore.getState().blocks;
            const block = currentBlocks.find(b => b.id === blockId);
            if (block?.bezierCurve) {
              const newPosition = mesh.position.clone(); // Clone to avoid reference issues
              const currentBezier = block.bezierCurve;
              const currentPoints = currentBezier.controlPoints ?? [];
              const newPoints = [...currentPoints];
              newPoints[pointIndex] = [newPosition.x, newPosition.y, newPosition.z];
              useMotionStore.getState().updateBlock(blockId, {
                bezierCurve: {
                  ...currentBezier,
                  controlPoints: newPoints
                }
              });
            }
            break;
          }
        }
      }
    };
    
    const handleDraggingChanged = (event: { value: boolean }) => {
      isDraggingRef.current = event.value;
      // Disable camera controls while dragging
      const controlsRef = useMotionStore.getState().controlsRef;
      if (controlsRef) {
        (controlsRef as any).enabled = !event.value;
      }
    };
    
    // Use 'objectChange' event which fires continuously during dragging
    (transformControl as any).addEventListener('objectChange', handleChange);
    (transformControl as any).addEventListener('dragging-changed', handleDraggingChanged);
    
    return () => {
      (transformControl as any).removeEventListener('objectChange', handleChange);
      (transformControl as any).removeEventListener('dragging-changed', handleDraggingChanged);
    };
  }, []);
  
  useFrame(() => {
    if (!transformControlRef.current || !showBezierDebug || isDraggingRef.current) return;
    
    // Update raycaster with current pointer position
    raycaster.setFromCamera(pointer, camera);
    
    // Get all control point meshes (only from active blocks)
    const controlPointMeshes = Array.from(controlPointRefsRef.current.values());
    
    // Find intersections
    const intersects = raycaster.intersectObjects(controlPointMeshes, false);
    
    if (intersects.length > 0) {
      const object = intersects[0].object as THREE.Mesh;
      
      // Get current attached object (if any)
      const currentObject = (transformControlRef.current as any).object;
      
      // If hovering over a different object, attach TransformControls
      if (object !== currentObject) {
        transformControlRef.current.attach(object);
      }
    }
  });
  
  // Find only the active bezier curve block and generate visualization data
  const bezierVisualizations = useMemo(() => {
    // Only show debug for the currently active block if it's a bezierCurve block
    if (!activeBlockId) return [];
    
    const activeBlock = blocks.find(block => block.id === activeBlockId);
    if (!activeBlock || !activeBlock.id.startsWith('bezierCurve') || !activeBlock.bezierCurve) {
      return [];
    }
    
    const bezierConfig = activeBlock.bezierCurve;
    
    // Get control points from array
    const controlPoints = bezierConfig.controlPoints 
      ? bezierConfig.controlPoints.map(p => new THREE.Vector3(...p))
      : [];
    
    // Generate curve points only if we have at least 2 points
    let curvePoints: THREE.Vector3[] | null = null;
    if (controlPoints.length >= 2) {
      const tension = bezierConfig.tension ?? 0.5;
      const curve = new THREE.CatmullRomCurve3(controlPoints, false, 'catmullrom');
      // Set tension property (only works with 'catmullrom' curveType)
      curve.tension = tension;
      curvePoints = [];
      const divisions = 100;
      for (let i = 0; i <= divisions; i++) {
        const t = i / divisions;
        curvePoints.push(curve.getPoint(t));
      }
    }
    
    return [{
      blockId: activeBlock.id,
      controlPoints,
      curvePoints,
      lookAtTarget: bezierConfig.lookAtTarget ? new THREE.Vector3(...bezierConfig.lookAtTarget) : null,
      isActive: true
    }];
  }, [blocks, activeBlockId]);
  
  // Don't render if debug is disabled (check after hooks)
  if (!showBezierDebug) return null;
  
  return (
    <>
      {bezierVisualizations.map((viz) => (
        <group key={viz.blockId}>
          {/* Control Points - Show each point with TransformControls */}
          {viz.controlPoints.map((point, index) => {
            const isStart = index === 0;
            const isEnd = index === viz.controlPoints.length - 1;
            
            // Color coding: start = green/red, middle = cyan/orange, end = blue/magenta
            const color = isStart 
              ? (viz.isActive ? "#00ff00" : "#ff0000")
              : isEnd
              ? (viz.isActive ? "#0000ff" : "#ff00ff")
              : (viz.isActive ? "#00ffff" : "#ff8800");
            
            const size = (isStart || isEnd) ? 0.1 : 0.08;
            
            return (
              <ControlPoint
                key={`${viz.blockId}-${index}`}
                blockId={viz.blockId}
                pointIndex={index}
                position={point}
                color={color}
                size={size}
                isActive={viz.isActive}
                controlPointRefsRef={controlPointRefsRef}
                transformControlRef={transformControlRef}
              />
            );
          })}
          
          
          {/* Curve Path - Show if we have at least 2 points */}
          {viz.curvePoints && viz.curvePoints.length > 0 && (
            <Line
              points={viz.curvePoints}
              color={viz.isActive ? "#ffff00" : "#ffffff"}
              lineWidth={viz.isActive ? 3 : 2}
            />
          )}
          
          {/* Look At Target (if set and we have control points) */}
          {viz.lookAtTarget && viz.controlPoints.length > 0 && (
            <>
              <Sphere position={viz.lookAtTarget} args={[0.12, 16, 16]}>
                <meshStandardMaterial color="#ffff00" />
              </Sphere>
              {/* Line from last point to target */}
              <Line
                points={[viz.controlPoints[viz.controlPoints.length - 1], viz.lookAtTarget]}
                color="#ffff00"
                lineWidth={1}
                dashed
              />
            </>
          )}
        </group>
      ))}
      
      {/* Single TransformControls instance shared across all control points */}
      <TransformControls
        ref={transformControlRef}
        mode="translate"
      />
    </>
  );
};
